import { getCurrentOperatorName } from '../../utils/currentOperator';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { provisionalSettleDate, resolveSettleDate, settlementCalendars, SettlementQuery } from '../../utils/settlement';
import OrderFills, { FillSummary } from './shared/OrderFills';
import {
  DerivativeMetricGrid,
  DerivativeMetricLabel,
  DerivativeMetricTile,
  DerivativeMetricValue,
  DerivativePreviewButton,
  DerivativePreviewToolbar,
  DerivativeReferenceHint
} from './shared/DerivativeTicketLayout';

import { DateField } from '../shared/DateField';

/**
 * Futures trade entry — SMF/economics split (same pattern as the options and
 * PC/LL tickets): the futures CONTRACT is defined once in Reference Data
 * (Derivatives > Futures -> POST /api/derivatives/futures/define) and this
 * ticket only references it and captures trade economics (direction,
 * contracts, price, commission, dates).
 *
 * buildTransactionData calls /prepare with the picked contractId — the backend
 * short-circuits to the existing contract/security and returns a fresh
 * per-trade sourceRef, so the posting engine routes to FuturesPostingSpi with
 * no reference-field re-entry and no silent instrument minting from a trade.
 */

// ADR 0009 §4.2: futures trade and settle on the CME calendar, not the book's. The ticket shows
// the settle date the server will store; the browser's count stands in until the server answers.
const settleQuery = (tradeDate: string, currency?: string): SettlementQuery =>
  ({ tradeDate, ticketType: 'FUTURES', convention: 'T+1', currency: currency || 'USD' });

/** Applies the server's settle date if the form still holds the inputs and the date it was asked about. */
const applyServerSettleDate = (setFormData: any, query: SettlementQuery, expected: string) => {
  resolveSettleDate(query).then(settleDate => setFormData((prev: any) =>
    prev.tradeDate === query.tradeDate && (prev.currency || 'USD') === query.currency
      && prev.settleDate === expected && settleDate && settleDate !== expected
      ? { ...prev, settleDate } : prev));
};

/** After a trade date change: the browser's count at once, then the server's. */
const refreshSettleDate = (setFormData: any, query: SettlementQuery) => {
  if (!query.tradeDate) return;
  const provisional = provisionalSettleDate(query);
  setFormData((prev: any) => ({ ...prev, settleDate: provisional }));
  applyServerSettleDate(setFormData, query, provisional);
};

// Built per mount (as the options ticket, F-HG-017): a module-level literal froze the default
// trade and settle dates at first import, across end-of-day rolls.
const makeInitialFormData = () => ({
  portfolio: '', assetManager: '', portfolioManager: '',
  // Instrument reference (locked; populated by picking a contract)
  contractSymbol: '', _existingContractId: '',
  contractName: '', underlyingAsset: '', assetClass: '', exchange: '', clearingHouse: '',
  contractMultiplier: '', tickSize: '', expiryDate: '', lastTradingDate: '', deliveryMethod: '',
  initialMarginPerContract: '', maintenanceMarginPerContract: '',
  ctdModifiedDuration: '', indexBeta: '', currency: 'USD',
  // Trade economics
  direction: 'LONG', contracts: '', tradePrice: '', commission: '',
  tradeDate: getTodayDate(calendarsFor('FUTURES')),
  settleDate: provisionalSettleDate(settleQuery(getTodayDate(calendarsFor('FUTURES')))),
  submittedBy: getCurrentOperatorName(), executedBy: '', notes: '',
});

const numOrNull = (v: any) => (v === '' || v == null ? null : Number(v));

const DerivativesFuturesTicketScreen: React.FC = () => {
  const initialFormData = React.useMemo(makeInitialFormData, []);
  const validateForm = (formData: any, action: string) => {
    const e: { [k: string]: string } = {};
    if (!formData.portfolio?.trim()) e.portfolio = 'Portfolio is required';
    // A weekend or holiday typed into the trade date leaves it empty (ADR 0009): refuse the save.
    if (!formData.tradeDate) e.tradeDate = 'Trade Date is required (a business day for this contract)';
    if (!formData._existingContractId) e.contractSymbol = 'Select a futures contract (or define one in Reference Data)';
    if (!formData.contracts || Number(formData.contracts) <= 0) e.contracts = 'Contracts > 0 required';
    if (formData.tradePrice === '' || formData.tradePrice == null || Number(formData.tradePrice) <= 0) e.tradePrice = 'Trade price > 0 required';
    if (action === 'execute' && !formData.executedBy?.trim()) e.executedBy = 'Executed By is required for execution';
    if (action === 'execute' && !(Number(formData._filledQty) > 0)) e.fills = 'Record at least one fill before executing';
    return e;
  };

  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio.trim());
    if (!portfolio) throw new Error(`Portfolio "${formData.portfolio}" not found`);
    if (!formData._existingContractId) throw new Error('Select a futures contract before saving');
    const isShort = formData.direction === 'SHORT';

    // Contract already defined in Reference Data — prepare short-circuits by
    // contractId and returns the minted security + a fresh per-trade sourceRef.
    const prepareRes = await axios.post('/api/derivatives/futures/prepare', {
      contractId: Number(formData._existingContractId),
      direction: formData.direction,
      contracts: numOrNull(formData.contracts),
      tradePrice: numOrNull(formData.tradePrice),
      tradeDate: formData.tradeDate,
      currency: formData.currency || 'USD',
    });
    const { securityId, sourceRef } = prepareRes.data;

    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager?.trim()) {
      const pm = dimensionData.portfolioManagers.find((x: any) => x.portfolioManagerName === formData.portfolioManager.trim());
      if (pm?.id) portfolioManagerId = pm.id;
    }
    let assetManagerId: number | null = null;
    if (formData.assetManager?.trim()) {
      const am = dimensionData.assetManagers.find((x: any) => x.assetManagerName === formData.assetManager.trim());
      if (am?.id) assetManagerId = am.id;
    }

    const contracts = Number(formData.contracts);
    const commission = formData.commission ? Number(formData.commission) : 0;
    // amount = contract notional / exposure (contracts × multiplier × price), so the
    // blotter "Amount" reads as a $ figure — not the bare contract count.
    const notional = contracts * Number(formData.contractMultiplier || 1) * Number(formData.tradePrice);
    return {
      transactionType: isShort ? 'SELL' : 'BUY',
      securityId, portfolioId: portfolio.id, assetManagerId, portfolioManagerId,
      quantity: contracts, price: Number(formData.tradePrice), amount: notional,
      netAmount: commission, commission,
      tradeDate: formData.tradeDate, settleDate: formData.settleDate,
      currency: formData.currency || 'USD', status: 'DRAFT',
      ticketType: 'FUTURES', eventType: 'ACQUIRE', sourceRef,
      createdBy: formData.submittedBy, executedBy: formData.executedBy || null,
      notes: formData.notes || `Futures ${formData.direction} ${contracts} ${formData.contractSymbol} @ ${formData.tradePrice}`,
    };
  };

  // Reopen/edit: repopulate from the saved trade + its futures contract.
  const loadTransactionData = async (t: any) => {
    const base = {
      ...initialFormData,
      portfolio: t.portfolioName || '', assetManager: t.assetManagerName || '', portfolioManager: t.portfolioManagerName || '',
      submittedBy: t.createdBy || getCurrentOperatorName(),
      executedBy: t.executedBy || '', notes: t.notes || '',
      tradeDate: t.tradeDate || initialFormData.tradeDate,
      settleDate: t.settleDate || provisionalSettleDate(settleQuery(t.tradeDate || initialFormData.tradeDate, t.currency)),
      currency: t.currency || 'USD',
      direction: t.transactionType === 'SELL' ? 'SHORT' : 'LONG',
      contracts: t.quantity != null ? String(Math.abs(Number(t.quantity))) : '',
      tradePrice: t.price != null ? String(t.price) : '',
      commission: t.commission != null ? String(t.commission) : '',
    };
    const m = (t.sourceRef || '').match(/futures-contract:(\d+)/);
    if (!m) return base;
    try {
      const c = (await axios.get(`/api/derivatives/futures/contract/${m[1]}`)).data;
      return {
        ...base,
        _existingContractId: String(c.contractId ?? c.id ?? m[1]),
        contractSymbol: c.contractSymbol || '', contractName: c.contractName || '',
        underlyingAsset: c.underlyingAsset || '', assetClass: c.assetClass || '',
        exchange: c.exchange || '', clearingHouse: c.clearingHouse || '',
        contractMultiplier: c.contractMultiplier != null ? String(c.contractMultiplier) : '',
        tickSize: c.tickSize != null ? String(c.tickSize) : '',
        expiryDate: c.expiryDate ? String(c.expiryDate).slice(0, 10) : '',
        lastTradingDate: c.lastTradingDate ? String(c.lastTradingDate).slice(0, 10) : '',
        deliveryMethod: c.deliveryMethod || '',
        initialMarginPerContract: c.initialMarginPerContract != null ? String(c.initialMarginPerContract) : '',
        maintenanceMarginPerContract: c.maintenanceMarginPerContract != null ? String(c.maintenanceMarginPerContract) : '',
        ctdModifiedDuration: c.ctdModifiedDuration != null ? String(c.ctdModifiedDuration) : '',
        indexBeta: c.indexBeta != null ? String(c.indexBeta) : '',
        currency: c.notionalCurrency || c.currency || base.currency,
      };
    } catch { return base; }
  };

  return (
    <BaseTicketScreen
      title="Futures Trade Entry" assetClass="Derivatives" assetSubClass="Futures"
      initialFormData={initialFormData} validateForm={validateForm} buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, handleInputChange, isFieldDisabled, dimensionData, transactionStatus, editingTransactionId }) => (
        <FuturesFields formData={formData} setFormData={setFormData} errors={errors} handleInputChange={handleInputChange}
          isFieldDisabled={isFieldDisabled} dimensionData={dimensionData} transactionStatus={transactionStatus}
          editingTransactionId={editingTransactionId} />
      )}
    </BaseTicketScreen>
  );
};

const FuturesFields: React.FC<{ formData: any; setFormData: any; errors: any; handleInputChange: (f: string, v: string) => void; isFieldDisabled: (f: string) => boolean; dimensionData: any; transactionStatus: any; editingTransactionId: number | null; }>
  = ({ formData, setFormData, errors, handleInputChange, isFieldDisabled, dimensionData, transactionStatus, editingTransactionId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [sugg, setSugg] = useState<any[]>([]);
  const [showSugg, setShowSugg] = useState(false);
  const dis = (f: string) => isFieldDisabled(f);

  // A new ticket asks the server for its settle date on open; a reopened trade keeps its own.
  useEffect(() => {
    const state: any = location.state;
    if (editingTransactionId || state?.transactionId || state?.id || !formData.tradeDate) return;
    applyServerSettleDate(setFormData, settleQuery(formData.tradeDate, formData.currency), formData.settleDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const picked = !!formData._existingContractId;
  const isRates = formData.assetClass === 'RATES';
  const isEquity = formData.assetClass === 'EQUITY_INDEX';

  const onSymbolChange = async (raw: string) => {
    const v = raw.toUpperCase();
    handleInputChange('contractSymbol', v);
    if (formData._existingContractId) handleInputChange('_existingContractId', '');
    if (!v.trim()) { setSugg([]); setShowSugg(false); return; }
    try {
      const res = await axios.get(`/api/derivatives/futures/contracts?symbol=${encodeURIComponent(v.trim())}`);
      setSugg(res.data || []); setShowSugg((res.data || []).length > 0);
    } catch { setSugg([]); setShowSugg(false); }
  };

  const pickContract = (c: any) => {
    handleInputChange('contractSymbol', c.contractSymbol || '');
    handleInputChange('contractName', c.contractName || '');
    handleInputChange('underlyingAsset', c.underlyingAsset || '');
    handleInputChange('assetClass', c.assetClass || '');
    handleInputChange('exchange', c.exchange || '');
    handleInputChange('clearingHouse', c.clearingHouse || '');
    handleInputChange('contractMultiplier', c.contractMultiplier != null ? String(c.contractMultiplier) : '');
    handleInputChange('tickSize', c.tickSize != null ? String(c.tickSize) : '');
    handleInputChange('expiryDate', c.expiryDate ? String(c.expiryDate).slice(0, 10) : '');
    handleInputChange('lastTradingDate', c.lastTradingDate ? String(c.lastTradingDate).slice(0, 10) : '');
    handleInputChange('deliveryMethod', c.deliveryMethod || '');
    handleInputChange('initialMarginPerContract', c.initialMarginPerContract != null ? String(c.initialMarginPerContract) : '');
    handleInputChange('maintenanceMarginPerContract', c.maintenanceMarginPerContract != null ? String(c.maintenanceMarginPerContract) : '');
    handleInputChange('ctdModifiedDuration', c.ctdModifiedDuration != null ? String(c.ctdModifiedDuration) : '');
    handleInputChange('indexBeta', c.indexBeta != null ? String(c.indexBeta) : '');
    if (c.currency || c.notionalCurrency) handleInputChange('currency', c.currency || c.notionalCurrency);
    handleInputChange('_existingContractId', String(c.contractId ?? c.id));
    setShowSugg(false);
  };

  const handleCalculate = async () => {
    try {
      setBusy(true);
      const res = await axios.post('/api/derivatives/futures/calculate', {
        assetClass: formData.assetClass, direction: formData.direction,
        contracts: numOrNull(formData.contracts), tradePrice: numOrNull(formData.tradePrice),
        contractMultiplier: numOrNull(formData.contractMultiplier), tickSize: numOrNull(formData.tickSize),
        initialMarginPerContract: numOrNull(formData.initialMarginPerContract),
        maintenanceMarginPerContract: numOrNull(formData.maintenanceMarginPerContract),
        ctdModifiedDuration: numOrNull(formData.ctdModifiedDuration), indexBeta: numOrNull(formData.indexBeta),
      });
      setPreview(res.data);
    } catch { setPreview(null); } finally { setBusy(false); }
  };
  const fmt = (v: any) => (v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));

  const refCell = (label: string, value: any) => (
    <div>
      <div style={{ color: '#64748b', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600, marginTop: 2 }}>{value || '—'}</div>
    </div>
  );

  return (
    <>
      {/* Row 1 — managers + execution */}
      <FormRow>
        <FormGroup>
          <Label>Asset Manager*</Label>
          <Select value={formData.assetManager || ''} disabled={dis('assetManager')} onChange={e => handleInputChange('assetManager', e.target.value)} className={errors.assetManager ? 'error' : ''}>
            <option value="">Select Asset Manager</option>
            {(dimensionData?.assetManagers || []).map((am: any) => <option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>)}
          </Select>
          {errors.assetManager && <FieldError>{errors.assetManager}</FieldError>}
        </FormGroup>
        <FormGroup>
          <Label>Portfolio Manager*</Label>
          <Select value={formData.portfolioManager || ''} disabled={dis('portfolioManager')} onChange={e => handleInputChange('portfolioManager', e.target.value)} className={errors.portfolioManager ? 'error' : ''}>
            <option value="">Select Portfolio Manager</option>
            {(dimensionData?.portfolioManagers || []).map((pm: any) => <option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>)}
          </Select>
          {errors.portfolioManager && <FieldError>{errors.portfolioManager}</FieldError>}
        </FormGroup>
        <FormGroup>
          <Label>Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
          <Select value={formData.executedBy || ''} disabled={dis('executedBy')} onChange={e => handleInputChange('executedBy', e.target.value)} className={errors.executedBy ? 'error' : ''}>
            <option value="">Select</option>
            {(dimensionData?.users || []).map((u: any) => <option key={u.id} value={u.fullName}>{u.fullName}{u.role ? ` (${u.role})` : ''}</option>)}
          </Select>
          {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* Row 2 — instrument reference (SMF): search-and-pick only */}
      <FormRow>
        <FormGroup style={{ gridColumn: '1 / -1' }}>
          <Label>Futures Contract* (defined in Reference Data)</Label>
          <div style={{ position: 'relative' }}>
            <Input placeholder="Search by contract symbol (e.g. ZN, ES)..." value={formData.contractSymbol || ''} disabled={dis('contractSymbol')}
              autoComplete="off"
              onChange={e => onSymbolChange(e.target.value)}
              onFocus={() => { if (sugg.length) setShowSugg(true); }}
              onBlur={() => setTimeout(() => setShowSugg(false), 150)}
              className={errors.contractSymbol ? 'error' : ''} />
            {showSugg && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4,
                background: '#0f172a', border: '1px solid rgba(148,163,184,0.3)', borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)', maxHeight: 220, overflowY: 'auto' }}>
                {sugg.map((c: any) => (
                  <div key={c.contractId} onMouseDown={() => pickContract(c)}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(59,130,246,0.12)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <div style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{c.contractSymbol}
                      <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 8 }}>{c.exchange}</span></div>
                    <div style={{ color: '#94a3b8', fontSize: 12 }}>{c.contractName || c.underlyingAsset}
                      {c.expiryDate ? ` · exp ${String(c.expiryDate).slice(0, 10)}` : ''}
                      {c.contractMultiplier != null ? ` · ×${c.contractMultiplier}` : ''}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {errors.contractSymbol && <FieldError>{errors.contractSymbol}</FieldError>}
          {!picked && (
            <DerivativeReferenceHint $tone="futures">
              No matching contract?{' '}
              <span style={{ color: '#60a5fa', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('/reference-data?tab=derivatives&assetClass=FUTURES&new=1')}>
                Define new futures contract →
              </span>{' '}
              (Reference Data · Derivatives · Futures)
            </DerivativeReferenceHint>
          )}
        </FormGroup>
      </FormRow>

      {/* Selected-contract reference card (read-only SMF attributes) */}
      {picked && (
        <div style={{ border: '1px solid rgba(148,163,184,0.18)', background: 'rgba(15,23,42,0.55)',
          borderRadius: 10, padding: '12px 16px', marginBottom: 16,
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
          {refCell('Underlying', `${formData.underlyingAsset}${formData.assetClass ? ` (${formData.assetClass})` : ''}`)}
          {refCell('Multiplier', formData.contractMultiplier ? `×${formData.contractMultiplier}` : '')}
          {refCell('Tick', formData.tickSize)}
          {refCell('Expiry', formData.expiryDate)}
          {refCell('Last Trading', formData.lastTradingDate)}
          {refCell('Delivery', formData.deliveryMethod)}
          {refCell('Exchange', formData.exchange)}
          {refCell('Clearing', formData.clearingHouse)}
          {refCell('Init Margin/Ct', formData.initialMarginPerContract)}
          {refCell('Maint Margin/Ct', formData.maintenanceMarginPerContract)}
          {isRates && refCell('CTD Mod Dur', formData.ctdModifiedDuration)}
          {isEquity && refCell('Index Beta', formData.indexBeta)}
          {refCell('Currency', formData.currency)}
        </div>
      )}

      {/* Row 3 — trade economics */}
      <FormRow>
        <FormGroup>
          <Label>Direction*</Label>
          <Select value={formData.direction} disabled={dis('direction')} onChange={e => handleInputChange('direction', e.target.value)}>
            <option value="LONG">Long</option><option value="SHORT">Short</option>
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>Contracts*</Label>
          <Input type="number" placeholder="10" value={formData.contracts || ''} disabled={dis('contracts')}
            onChange={e => handleInputChange('contracts', e.target.value)} className={errors.contracts ? 'error' : ''} />
          {errors.contracts && <FieldError>{errors.contracts}</FieldError>}
        </FormGroup>
        <FormGroup>
          <Label>Trade Price* (points)</Label>
          <Input type="number" placeholder="112.50" value={formData.tradePrice || ''} disabled={dis('tradePrice')}
            onChange={e => handleInputChange('tradePrice', e.target.value)} className={errors.tradePrice ? 'error' : ''} />
          {errors.tradePrice && <FieldError>{errors.tradePrice}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* Row 4 — dates + commission */}
      <FormRow>
        <FormGroup>
          <Label>Trade Date*</Label>
          <DateField
            value={formData.tradeDate || ''}
            disabled={dis('tradeDate')}
            calendars={calendarsFor('FUTURES', formData.currency)}
            onChange={iso => {
              handleInputChange('tradeDate', iso);
              refreshSettleDate(setFormData, settleQuery(iso, formData.currency));
            }}
          />
        </FormGroup>
        <FormGroup>
          <Label>Settle Date*</Label>
          <DateField
            value={formData.settleDate || ''}
            disabled={dis('settleDate')}
            calendars={settlementCalendars({ ticketType: 'FUTURES', currency: formData.currency })}
            onChange={iso => handleInputChange('settleDate', iso)}
          />
        </FormGroup>
        <FormGroup>
          <Label>Commission</Label>
          <Input type="number" placeholder="100" value={formData.commission || ''} disabled={dis('commission')}
            onChange={e => handleInputChange('commission', e.target.value)} />
        </FormGroup>
      </FormRow>

      <DerivativePreviewToolbar>
        <DerivativePreviewButton type="button" onClick={handleCalculate} disabled={busy || !picked} $tone="futures">
          {busy ? 'Calculating...' : 'Preview Notional & Margin'}
        </DerivativePreviewButton>
      </DerivativePreviewToolbar>
      {preview && (
        <DerivativeMetricGrid>
          <Cell label="Notional / Exposure" value={`$${fmt(preview.notional)}`} />
          {preview.tickValue != null && <Cell label="Tick Value" value={`$${fmt(preview.tickValue)}`} />}
          {preview.initialMargin != null && <Cell label="Initial Margin" value={`$${fmt(preview.initialMargin)}`} />}
          {preview.maintenanceMargin != null && <Cell label="Maintenance Margin" value={`$${fmt(preview.maintenanceMargin)}`} />}
          {preview.dv01 != null && <Cell label="DV01 (+1bp)" value={`$${fmt(preview.dv01)}`} />}
          {preview.betaAdjustedExposure != null && <Cell label="Beta-Adj Exposure" value={`$${fmt(preview.betaAdjustedExposure)}`} />}
        </DerivativeMetricGrid>
      )}

      <OrderFills tradeId={editingTransactionId} status={transactionStatus} orderPrice={formData.tradePrice}
        onSummary={(s: FillSummary) => {
          const v = String(s.filledQuantity);
          if (v !== (formData._filledQty ?? '')) handleInputChange('_filledQty', v);
        }} />
    </>
  );
};

const Cell: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <DerivativeMetricTile $tone="futures">
    <DerivativeMetricLabel>{label}</DerivativeMetricLabel>
    <DerivativeMetricValue $tone="futures">{value}</DerivativeMetricValue>
  </DerivativeMetricTile>
);

export default DerivativesFuturesTicketScreen;
